import React from 'react'
import {
  useGroupWithMembersData,
  AddCompanyGroupMembers_addCompanyGroupMembers,
  MemberFormValues,
  useStateWithDebounce,
} from '@modmd/data'
import { Box } from 'components/Layout'
import Datalist from 'components/Datalist'
import Pagination from 'components/Pagination'
import { DEVICE } from 'theme'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import Button from 'components/Button'
import Icon from 'components/Icon'
import TextInput from 'components/TextInput'
import { CsvInviteNewGroupMembers } from '../CsvInviteNewGroupMembers'

interface Props {
  companyId: string
  groupId: string
  isSelectedAll: boolean
  includedUserIds: string[]
  excludedUserIds: string[]
  onToggleAll: (isSelectedAll: boolean) => void
  onAddMember: (user: MemberFormValues) => void
  onRemoveMember: (userId: string) => void
  onCsvUploadSuccess?: (users: AddCompanyGroupMembers_addCompanyGroupMembers[]) => void
  page: number
  onPageChange: (page: number) => void
}

const GroupMembersSelector: React.VFC<Props> = ({
  companyId,
  groupId,
  page,
  includedUserIds,
  excludedUserIds,
  onPageChange,
  onToggleAll,
  isSelectedAll,
  onAddMember,
  onRemoveMember,
  onCsvUploadSuccess,
}) => {
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const [emailOrNameLike, setEmailOrNameLike, debouncedEmailOrNameLike] = useStateWithDebounce('')
  const groupWithMembersQuery = useGroupWithMembersData({
    groupId,
    membersPage: page,
    filterData: {
      emailOrNameLike: debouncedEmailOrNameLike,
    },
    membersSorter: {
      sortBy: 'users.id',
    },
  })
  const { pagination } = groupWithMembersQuery.data?.group?.Members || {}
  return (
    <>
      <Datalist
        header={
          <Datalist.Title
            rightContent={
              <>
                <TextInput
                  hasBottomContent={false}
                  hasTopContent={false}
                  placeholder="Search member"
                  value={emailOrNameLike}
                  onChange={(value) => setEmailOrNameLike(value.target.value)}
                />
                <Button colorVariant="primary" onClick={() => onToggleAll(!isSelectedAll)}>
                  {isSelectedAll ? `Deselect all` : `Select all`}
                </Button>
                {onCsvUploadSuccess && (
                  <CsvInviteNewGroupMembers
                    companyId={companyId}
                    groupId={groupId}
                    onUploadSuccess={onCsvUploadSuccess}
                  />
                )}
              </>
            }
          >
            Group members
          </Datalist.Title>
        }
        isLoading={groupWithMembersQuery.loading}
        data={groupWithMembersQuery.data?.group?.Members?.Members || []}
        shouldRenderTableHeader={isDesktop}
        showActionIndicators
        renderTableHeader={() => (
          <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
            <Datalist.HeaderCell label="Email" />
            <Datalist.HeaderCell label="First name" />
            <Datalist.HeaderCell label="Last name" />
            <Datalist.HeaderCell label="Action" />
          </Datalist.Row>
        )}
        renderTableRow={(Member) => {
          const isMemberIncluded = includedUserIds.some((userId) => userId === Member.User.id)
          const isMemberExcludedFromGroup = excludedUserIds.some((id) => id === Member.User.id)
          const canRemoveMember = isSelectedAll ? !isMemberExcludedFromGroup : isMemberIncluded
          return (
            <Datalist.Row display="grid" gridTemplateColumns="repeat(4, 1fr)">
              <Datalist.Cell label="Email" useEllipsis>
                {Member.User.email}
              </Datalist.Cell>
              <Datalist.Cell label="First name" useEllipsis>
                {Member.User.firstName}
              </Datalist.Cell>
              <Datalist.Cell label="Last name" useEllipsis>
                {Member.User.lastName}
              </Datalist.Cell>
              <Datalist.Cell label="Action">
                {canRemoveMember ? (
                  <Button
                    onClick={() => {
                      void onRemoveMember(Member.User.id)
                    }}
                    appearance="ghost"
                    colorVariant="danger"
                    size="small"
                  >
                    <Icon.Close size="L" />
                  </Button>
                ) : (
                  <Button
                    size="small"
                    onClick={() => {
                      onAddMember({
                        User: {
                          ...Member.User,
                          email: Member.User.email!,
                          parentId: Member.User.parentId ?? undefined,
                        },
                        isMain: false,
                        symptoms: [],
                      })
                    }}
                  >
                    Add
                  </Button>
                )}
              </Datalist.Cell>
            </Datalist.Row>
          )
        }}
      />
      {pagination && (
        <Box margin="1.5rem 0">
          <Pagination
            firstPage={1}
            lastPage={Math.ceil(pagination.total / pagination.length)}
            currentPage={page}
            onPageChange={onPageChange}
          />
        </Box>
      )}
    </>
  )
}

export { GroupMembersSelector }
